// import { Icon } from "@iconify/react";
// import { AppBar, Drawer, IconButton, Toolbar, Typography } from "@mui/material";
// import React from "react";
// import { Outlet, useNavigate } from "react-router-dom";

// export default function DashboardLayout() {
//   const navigate = useNavigate();
//   const handleLogout = () => {
//     navigate("/logout");
//   };
//   return (
//     <div>
//       <AppBar position="static">
//         <Toolbar>
//           <Typography variant="h6" sx={{ flexGrow: 1 }}>
//             Eventegration Control Panel
//           </Typography>
//           <IconButton color="inherit" onClick={handleLogout}>
//             <Icon icon="majesticons:door-exit" />
//           </IconButton>
//         </Toolbar>
//       </AppBar>
//       <Drawer open={true}>hi</Drawer>
//       <Outlet />;
//     </div>
//   );
// }

import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  styled,
  useTheme,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  BarChart as BarChartIcon,
  QrCodeScanner as QrCodeScannerIcon,
  Settings as SettingsIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  ChevronLeft as ChevronLeftIcon,
  ExitToApp as LogoutIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Store as StoreIcon,
  GroupAdd as GroupAddIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_MY_AUTH } from "../graphql/queries";
import { useNavigate } from "react-router-dom";
import { ColorModeContext } from "../theme/ThemeProvider";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { jotunLightTheme } from "../theme/jotunTheme";

const JOTUN_LOGO_URL =
  "https://api-admin.eventegration.com/public/logo/5087644490.png";

// Styled components for enhanced UI
const drawerWidth = 260;

const OpenedDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    background:
      theme.palette.mode === "dark"
        ? theme.palette.background.default
        : theme.palette.primary.main,
    color:
      theme.palette.mode === "dark"
        ? theme.palette.text.primary
        : theme.palette.primary.contrastText,
    boxShadow: theme.shadows[4],
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  },
}));

const ClosedDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
    background:
      theme.palette.mode === "dark"
        ? theme.palette.background.default
        : theme.palette.primary.main,
    color:
      theme.palette.mode === "dark"
        ? theme.palette.text.primary
        : theme.palette.primary.contrastText,
    boxShadow: theme.shadows[4],
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
  },
}));

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  boxShadow: theme.shadows[3],
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Main = styled("main")(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  height: "100vh",
  overflow: "auto",
  background:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : theme.palette.background.paper,
  marginTop: 64, // AppBar height
}));

// Modify the DashboardLayout component
const DashboardLayout = ({ children }) => {
  const theme = useTheme();
  const colorMode = React.useContext(ColorModeContext);
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();

  const { data: authData } = useQuery(GET_MY_AUTH);
  const isAdmin = authData?.me === "admin";
  const eventId = authData?.eventId;
  const isCouponEvent = eventId === "5087644490";

  console.log(
    "User role:",
    authData?.me,
    "IsAdmin:",
    isAdmin,
    "EventId:",
    eventId,
  );

  // Define navigation items based on admin status
  const getNavItems = () => {
    // Attendee + coupon event: only two items — Coupon claimed (dashboard) and Scan QR
    if (isCouponEvent && !isAdmin) {
      return [
        { name: "Coupon claimed", icon: <AssessmentIcon />, path: "/" },
        {
          name: "Scan QR",
          icon: <QrCodeScannerIcon />,
          path: "/scanning-visitors",
        },
      ];
    }

    // For coupon event admin, home is Dashboard (Coupon Progress)
    const firstItem = isCouponEvent
      ? { name: "Dashboard", icon: <AssessmentIcon />, path: "/" }
      : { name: "Dashboard", icon: <DashboardIcon />, path: "/" };
    const secondItem = isCouponEvent
      ? { name: "Coupons Claimed", icon: <PeopleIcon />, path: "/my-visitors" }
      : { name: "My Visitors", icon: <PeopleIcon />, path: "/my-visitors" };
    const thirdItem = isCouponEvent
      ? {
          name: "Scan Coupons QR",
          icon: <QrCodeScannerIcon />,
          path: "/scanning-visitors",
        }
      : {
          name: "Scan Visitors",
          icon: <QrCodeScannerIcon />,
          path: "/scanning-visitors",
        };
    const commonItems = [firstItem, secondItem, thirdItem];

    // Admin-only items
    const adminItems = [
      ...commonItems,
      { name: "Insights", icon: <BarChartIcon />, path: "/insights" },
    ];

    if (isCouponEvent && isAdmin) {
      adminItems.push({
        name: "Attendees/Shops",
        icon: <StoreIcon />,
        path: "/attendees",
      });
    }

    /*
     * TEAM — how an admin creates the staff accounts that do the scanning.
     *
     * The page and its protected route at /staff never went anywhere; only the way IN did, when
     * the nav was reshaped around the coupon event. The feature stayed reachable by typing the
     * URL and by nothing else, so an admin setting up a NEW event had no way to add their
     * scanners — found the night before IEEE, which is exactly when it was needed.
     *
     * Admin only, and last, so the three everyday items keep the positions people already reach
     * for. Not gated by event: adding staff is what every lead-gen event does, and the coupon
     * event has its own Attendees/Shops above for its different shape.
     */
    adminItems.push({
      name: "Team",
      icon: <GroupAddIcon />,
      path: "/staff",
    });

    return isAdmin ? adminItems : commonItems;
  };

  // Secondary nav items
  const getSecondaryNavItems = () => {
    const commonItems = [
      { name: "Settings", icon: <SettingsIcon />, path: "/settings" },
    ];

    // Admin-only: Email Template and Email Settings (for coupon event)
    // if (isCouponEvent && isAdmin) {
    //   commonItems.unshift(
    //     { name: "Email Templates", icon: <EmailIcon />, path: "/email-templates" },
    //     { name: "Email Settings", icon: <EmailIcon />, path: "/email-settings" },
    //   );
    // }

    return commonItems;
  };

  // Get navigation items
  const mainNavItems = getNavItems();
  const secondaryNavItems = getSecondaryNavItems();

  // Close drawer on mobile by default
  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    }
  }, [isMobile]);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  // Get current page name for the app bar
  const getCurrentPageName = () => {
    const currentPath = location.pathname;
    const navItem = [...mainNavItems, ...secondaryNavItems].find(
      (item) => item.path === currentPath,
    );
    return navItem?.name || "Dashboard";
  };

  const layoutContent = (
    <Box sx={{ display: "flex" }}>
      <StyledAppBar position="fixed" open={open}>
        <Toolbar sx={{ pr: 2 }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{ marginRight: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            {getCurrentPageName()}
          </Typography>

          {/* Scanner shortcut for quick access */}
          <Tooltip title="Scan Badge">
            <IconButton
              color="inherit"
              sx={{ mr: 1 }}
              onClick={() => navigate("/scanning-visitors")}
            >
              <QrCodeScannerIcon />
            </IconButton>
          </Tooltip>

          {/* Theme toggle */}
          <Tooltip
            title={theme.palette.mode === "dark" ? "Light Mode" : "Dark Mode"}
          >
            <IconButton
              color="inherit"
              onClick={colorMode.toggleColorMode}
              sx={{ mr: 1 }}
            >
              {theme.palette.mode === "dark" ? (
                <LightModeIcon />
              ) : (
                <DarkModeIcon />
              )}
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          {/* <Tooltip title="Notifications">
            <IconButton color="inherit" sx={{ mr: 1 }}>
              <Badge badgeContent={4} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip> */}

          {/* User menu */}
          <Tooltip title="Account">
            <IconButton
              onClick={handleMenu}
              color="inherit"
              sx={{ ml: 1 }}
              aria-controls="menu-appbar"
              aria-haspopup="true"
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: theme.palette.secondary.main,
                }}
              >
                {authData?.name?.charAt(0) || "U"}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle1">
                {authData?.name || "User"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {authData?.company || "Company"}
              </Typography>
            </Box>
            <Divider />
            {/* <MenuItem onClick={handleClose}>Profile</MenuItem> */}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </StyledAppBar>

      {open ? (
        <OpenedDrawer variant="permanent" open={open}>
          <Toolbar
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              px: [1],
            }}
          >
            <Box
              sx={{ flexGrow: 1, pl: 1, display: "flex", alignItems: "center" }}
            >
              {isCouponEvent ? (
                <img
                  src={JOTUN_LOGO_URL}
                  alt="Logo"
                  style={{
                    maxHeight: 40,
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Typography variant="h6" component="div">
                  LeadGen
                </Typography>
              )}
            </Box>
            <IconButton onClick={toggleDrawer} color="inherit">
              <ChevronLeftIcon />
            </IconButton>
          </Toolbar>
          <Divider />
          <List component="nav">
            {/* Main nav items */}
            {mainNavItems.map((item) => (
              <ListItem key={item.name} disablePadding sx={{ my: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={location.pathname === item.path}
                  sx={{
                    borderRadius: "0 24px 24px 0",
                    mr: 1,
                    "&.Mui-selected": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? theme.palette.action.selected
                          : "rgba(255, 255, 255, 0.12)",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? theme.palette.action.hover
                            : "rgba(255, 255, 255, 0.2)",
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color:
                        theme.palette.mode === "dark"
                          ? "inherit"
                          : "rgba(255, 255, 255, 0.7)",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.name} />
                </ListItemButton>
              </ListItem>
            ))}

            <Divider sx={{ my: 2 }} />

            {/* Secondary nav items */}
            {secondaryNavItems.map((item) => (
              <ListItem key={item.name} disablePadding sx={{ my: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={location.pathname === item.path}
                  sx={{
                    borderRadius: "0 24px 24px 0",
                    mr: 1,
                    "&.Mui-selected": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? theme.palette.action.selected
                          : "rgba(255, 255, 255, 0.12)",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? theme.palette.action.hover
                            : "rgba(255, 255, 255, 0.2)",
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color:
                        theme.palette.mode === "dark"
                          ? "inherit"
                          : "rgba(255, 255, 255, 0.7)",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </OpenedDrawer>
      ) : (
        <ClosedDrawer variant="permanent">
          <Toolbar
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: [1],
            }}
          >
            {isCouponEvent ? (
              <img
                src={JOTUN_LOGO_URL}
                alt="Logo"
                style={{ maxHeight: 36, maxWidth: 36, objectFit: "contain" }}
              />
            ) : (
              <IconButton color="inherit">
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
          <Divider />
          <List component="nav">
            {/* Main nav items */}
            {mainNavItems.map((item) => (
              <ListItem
                key={item.name}
                disablePadding
                sx={{ display: "block" }}
              >
                <Tooltip title={item.name} placement="right">
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    selected={location.pathname === item.path}
                    sx={{
                      minHeight: 48,
                      justifyContent: "center",
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: "auto",
                        justifyContent: "center",
                        color:
                          theme.palette.mode === "dark"
                            ? "inherit"
                            : "rgba(255, 255, 255, 0.7)",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}

            <Divider sx={{ my: 2 }} />

            {/* Secondary nav items */}
            {secondaryNavItems.map((item) => (
              <ListItem
                key={item.name}
                disablePadding
                sx={{ display: "block" }}
              >
                <Tooltip title={item.name} placement="right">
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    selected={location.pathname === item.path}
                    sx={{
                      minHeight: 48,
                      justifyContent: "center",
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: "auto",
                        justifyContent: "center",
                        color:
                          theme.palette.mode === "dark"
                            ? "inherit"
                            : "rgba(255, 255, 255, 0.7)",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </ClosedDrawer>
      )}

      <Main>
        <Box sx={{ py: 1 }}>{children}</Box>
      </Main>
    </Box>
  );

  return isCouponEvent ? (
    <MuiThemeProvider theme={jotunLightTheme}>{layoutContent}</MuiThemeProvider>
  ) : (
    layoutContent
  );
};

export default DashboardLayout;
